import * as React from 'react';

import cn from 'classnames';
import tinytime from 'tinytime';
import { Message as AIMessage } from '@ai-sdk/react';

import { useBuilderContext } from '@components/BuilderApp/BuilderContext';
import { AvatarFromId, Spinner, Text } from '@components/common';
import { Preview as MarkdownPreview } from '@components/common/MarkdownEditor';
import { useUser } from '@hooks/useUser';

import { Reasoning } from './Reasoning';
import { ToolCall } from './ToolCall';
import { ToolInvocation } from './types';

const dateTemplate = tinytime('{MM} {DD}');
const timeTemplate = tinytime('{h}:{mm}{a}');

interface MessageProps {
  message: AIMessage;
  loading?: boolean;
  isUserMessage?: boolean;
}

const BotAvatar: React.FC<{ size?: 'sm' | 'md' | 'lg' | 'xl' }> = ({ size = 'md' }) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
    xl: 'h-10 w-10'
  };

  return (
    <div
      className={`${sizeClasses[size]} flex items-center justify-center rounded-full border border-gray-200 bg-white`}
    >
      <img src='/apple-icon.png' alt='Great Question' className='h-4/5 w-4/5 object-contain' />
    </div>
  );
};

export const Message: React.FC<MessageProps> = ({ message, loading = false, isUserMessage = false }) => {
  const { openContent } = useBuilderContext();
  const user = useUser();

  const handleOpenContent = () => openContent?.(message.id);

  const renderMessageContent = () => {
    if (!message.parts) {
      return <MarkdownPreview className='text-sm'>{message.content}</MarkdownPreview>;
    }

    return (
      <>
        {message.parts.map((part, index) => (
          <div key={index}>
            {part.type === 'text' && <MarkdownPreview className='text-sm'>{part.text}</MarkdownPreview>}
            {part.type === 'tool-invocation' && (
              <ToolCall
                toolInvocation={part.toolInvocation as ToolInvocation}
                openContent={handleOpenContent}
                message={message}
              />
            )}
            {part.type === 'reasoning' && <Reasoning reasoning={part.reasoning} />}
          </div>
        ))}
      </>
    );
  };

  const displayName = message.role === 'user' ? user?.first_name || 'You' : 'Great Question';

  const renderTimestamp = () => {
    if (!message.createdAt || isUserMessage) return null;

    const date = new Date(message.createdAt);
    const dateStr = dateTemplate.render(date);
    const timeStr = timeTemplate.render(date);

    return (
      <Text h='200' color='gray-500' className='flex items-center'>
        {dateStr}
        <span className='mx-1'>·</span>
        {timeStr}
      </Text>
    );
  };

  return (
    <div
      className={cn('flex space-x-3', {
        'justify-end': isUserMessage
      })}
    >
      {!isUserMessage && (
        <div className='flex-shrink-0'>
          {message.role === 'user' ? <AvatarFromId userId={user?.id} size='md' /> : <BotAvatar size='md' />}
        </div>
      )}

      {isUserMessage && loading && (
        <div className='flex items-center'>
          <Spinner className='h-4 w-4' />
        </div>
      )}

      <div
        className={cn('min-w-0', {
          'flex-1': !isUserMessage,
          'max-w-md': isUserMessage
        })}
      >
        {!isUserMessage && (
          <div className='mb-1 flex items-center space-x-2'>
            <Text h='400' bold>
              {displayName}
            </Text>
            {renderTimestamp()}
          </div>
        )}

        <div
          className={cn('rounded-lg py-2', {
            'max-w-lg bg-white pr-4': !isUserMessage,
            'rounded-sm bg-gray-100 px-4': isUserMessage
          })}
        >
          <div className={cn('inline-flex items-start gap-3', { 'text-gray-500': loading })}>
            <div className={cn({ '-mb-2': isUserMessage })}>{renderMessageContent()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
